import { useEffect, useState } from 'react'

import {
  useLazyCompaniesForSelectQuery,
  useLazyGetCompanyNameFromIdQuery,
} from '../../../../store/api'
import type {
  MetaResponse,
  PaginatedQueryParams,
} from '../../../../store/types'
import type { CompanyForSelect } from '../../../../store/types/company'
import type { DropdownOption, NegativeInfoDropdownOption } from '../../types'
import AsyncPaginatedDropdown from '../async-paginate'
import CreatebleAsyncPaginatedDropdown from '../createable-async-paginate'
import SeparatableAsyncPaginateDropdown from '../separator-async-paginate'

type CompanyDropdownProps = {
  name?: string
  onChange?: (items: NegativeInfoDropdownOption[]) => void
  onChangeForManageTable?: (item: DropdownOption) => void
  onSubmit?: () => void
  isClearable?: boolean
  value?: DropdownOption[]
  isCreatable?: boolean
  isMulti?: boolean
  customComponent?: boolean
  separator?: string
  setSeparator?: (value: string | undefined) => void
  filterValues?: string
  canBeNegative?: boolean
  customStyles?: any
}

const companyToDropdownOptions = (
  company: CompanyForSelect,
): DropdownOption => ({
  label: company.text,
  value: company.id.toString(),
})

export default function CompanyDropdown({
  name,
  onChange,
  onChangeForManageTable,
  onSubmit,
  value,
  isClearable = false,
  isCreatable = true,
  isMulti = false,
  customComponent = false,
  separator,
  setSeparator,
  filterValues,
  canBeNegative = false,
  customStyles,
}: CompanyDropdownProps) {
  const [fetcher] = useLazyCompaniesForSelectQuery()
  const companiesFetcher = async (params: PaginatedQueryParams) => {
    const res = await fetcher(params)
    const response: {
      data?: DropdownOption[]
      meta?: MetaResponse<CompanyForSelect>
    } = {}

    response.data = res.data?.data.map(companyToDropdownOptions)
    response.meta = res.data?.meta

    return response
  }

  const [defaultCompanyValues, setDefaultCompanyValues] = useState<
    Array<{ label: string; value: string }>
  >([])

  const [getCompanyNames, { isLoading }] = useLazyGetCompanyNameFromIdQuery()

  useEffect(() => {
    if (filterValues) {
      getCompanyNames({ ids: filterValues })
        .then((response) => {
          const items = response?.data.map((item: any) => ({
            label: item?.text,
            value: item?.id.toString(),
            isNegative: item?.is_negative,
          }))

          const idList = filterValues
            .split(',')
            .map((item: string) => Math.abs(Number(item)).toFixed())
          items.sort(
            (i1: any, i2: any) =>
              idList.indexOf(i1.value) - idList.indexOf(i2.value),
          )
          setDefaultCompanyValues(items)
        })
        .catch((err) => {})
    } else {
      setDefaultCompanyValues([])
    }
  }, [filterValues])

  let Element = AsyncPaginatedDropdown
  if (isCreatable) {
    Element = CreatebleAsyncPaginatedDropdown
  }
  if (customComponent) {
    return (
      <SeparatableAsyncPaginateDropdown
        isReady={!isLoading}
        fetcher={companiesFetcher}
        defaultValue={defaultCompanyValues}
        onChange={onChange}
        onSubmit={onSubmit}
        isClearable={isClearable}
        isMulti={isMulti}
        value={value}
        customComponent={customComponent}
        separator={separator}
        setSeparator={setSeparator}
        canBeNegative={canBeNegative}
        customStyles={customStyles}
      />
    )
  } else
    return (
      <Element
        key={defaultCompanyValues[0]?.value}
        fetcher={companiesFetcher}
        name={name}
        defaultValue={defaultCompanyValues}
        onChange={onChangeForManageTable ?? onChange}
        isClearable={isClearable}
        isMulti={isMulti}
        value={value}
        customComponent={customComponent}
        separator={separator}
        setSeparator={setSeparator}
      />
    )
}
